import React from 'react';
import { ArrowLeft, Users, MessageSquare, Edit, Receipt } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table } from '@/types';

interface TableHeaderProps {
  table: Table;
  onNavigateBack: () => void;
  onEditSeats: () => void;
  onEditObservations: () => void;
  onSyncTable?: () => void;
}

const TableHeader: React.FC<TableHeaderProps> = ({
  table,
  onNavigateBack,
  onEditSeats,
  onEditObservations,
  onSyncTable,
}) => {
  const getTableStatusClass = (status: string) => {
    switch (status) {
      case 'available':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'occupied':
        return 'bg-rose-100 text-rose-800 border-rose-200';
      case 'reserved':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'cancelled':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getTableStatus = (status: string) => {
    switch (status) {
      case 'available':
        return 'Disponível';
      case 'occupied':
        return 'Ocupada';
      case 'reserved':
        return 'Reservada';
      case 'cancelled':
        return 'Cancelada';
      default:
        return status;
    }
  };

  return (
    <div className="mb-6 bg-card border rounded-lg p-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-4 min-w-0">
        <Button 
          variant="outline" 
          size="icon" 
          onClick={onNavigateBack}
          className="h-10 w-10"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        
        <div className="min-w-0">
          <h1 className="text-2xl font-bold">Mesa {table.number}</h1>
          <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-2">
            <Badge className={getTableStatusClass(table.status)}>
              {getTableStatus(table.status)}
            </Badge>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={onEditSeats}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
            >
              <Users className="h-4 w-4" />
              {table.seats} lugares
              <Edit className="h-3 w-3" />
            </Button>
            
            {table.observations && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onEditObservations}
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
              >
                <MessageSquare className="h-4 w-4" />
                Observações
                <Edit className="h-3 w-3" />
              </Button>
            )}
          </div>
        </div>
      </div>
      
      <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:justify-end">
        {/* Botão de sincronização */}
        {onSyncTable && table.orders.some(order => order.status === 'open' || (order.status === 'paid' && (!order.syncStatus || order.syncStatus === 'pending'))) && (
          <Button
            variant="outline"
            size="sm"
            onClick={onSyncTable}
            className="flex items-center justify-center gap-2 border-blue-300 text-blue-600 hover:bg-blue-50 w-full sm:w-auto"
          >
            <Receipt className="h-4 w-4" />
            Sincronizar
          </Button>
        )}
        
        <div className="text-right sm:text-right">
          <p className="text-sm text-muted-foreground">
            {table.orders.length} {table.orders.length === 1 ? 'pedido' : 'pedidos'}
          </p>
          {table.timeOpened && (
            <p className="text-sm text-muted-foreground">
              Aberta às {new Date(table.timeOpened).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default TableHeader;